import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { experienceService } from '../services/experience.service';
import type { CreateExperienceRequest, Experience } from '../types/experience.types';

interface CreateExperienceData {
  candidateId: string;
  request: CreateExperienceRequest;
}

type CreateMutation = UseMutationResult<Experience, Error, CreateExperienceData>;

interface UseCreateExperienceReturn {
  createExperience: CreateMutation['mutate'];
  createExperienceAsync: CreateMutation['mutateAsync'];
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  error: Error | null;
  reset: () => void;
}

export function useCreateExperience(): UseCreateExperienceReturn {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (data: CreateExperienceData) =>
      experienceService.createExperience(data.candidateId, data.request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['experiences', { candidateId: variables.candidateId }],
      });
    },
  });

  return {
    createExperience: mutation.mutate,
    createExperienceAsync: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error,
    reset: mutation.reset,
  };
}
